import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import {
  ALL_PRODUCT_TYPES,
  ChartTypeLabels,
  getChartTypeFlag,
  getMidstreamChartTypes
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { updateChartByPath } from "store/features";

import { useChartEntities } from "hooks/charts";
import useFeatureFlags from "hooks/useFeatureFlags";

export const useChartTypeHandler = () => {
  const { id } = useChartsContext();
  const { chartType } = useChartEntities(id);
  const dispatch = useDispatch();
  const { hasFeature } = useFeatureFlags();
  const availableMidstreamChartTypes = useMemo(
    () => getMidstreamChartTypes(hasFeature("Midstream Allocation Model")),
    [hasFeature]
  );

  useEffect(() => {
    // the midstream charts that exist on the well charts are the same to keep functionality
    // the same in existing logic (such as below) but for features, highlighting and requests
    // it's identified as a different chart
    const originalChartType = chartType?.replace(" Midstream", "");
    switch (chartType) {
      case EvaChart.WellContribution:
        if (
          !availableMidstreamChartTypes[0].items.includes(
            originalChartType as ChartTypeLabels
          )
        ) {
          // display TotalRateDate chart if WellContribution is selected but not available
          dispatch(
            updateChartByPath({
              id,
              path: "chartType",
              value: EvaChart.TotalRateDate
            })
          );
          dispatch(
            updateChartByPath({
              id,
              path: "product",
              value: ALL_PRODUCT_TYPES.GasInlet.label
            })
          );
        }
        break;
    }

    // EVA-10: a chart persisted (sessionStorage / saved workspace) with a
    // chart type whose feature flag has since been disabled would otherwise
    // keep rendering and requesting data even though it's been removed from
    // the chart-type menu (filterChartTypeGroups). Mirrors the WellContribution
    // reset above: fall back to the entity default (TotalRateDate) chart type.
    const flag = getChartTypeFlag(originalChartType as ChartTypeLabels);
    if (flag && !hasFeature(flag)) {
      dispatch(
        updateChartByPath({
          id,
          path: "chartType",
          value: EvaChart.TotalRateDate
        })
      );
    }
  }, [chartType, dispatch, availableMidstreamChartTypes, hasFeature]);
};
